/**
 * IP白名单检查工具
 */
import { checkIPWhitelist } from '@/api/system'

// IP检查结果缓存
let ipCheckCache = {
  ip: null,
  allowed: null,
  timestamp: null,
  ttl: 5 * 60 * 1000 // 5分钟缓存
}

/**
 * 获取用户IP地址
 * @returns {Promise<string>} IP地址
 */
export async function getUserIP() {
  try {
    // 在实际部署中,应该从服务器端获取真实IP
    // 这里提供多种获取方式

    // 方式1: 从服务器API获取 (推荐)
    const response = await fetch('/api/system/get-client-ip')
    if (response.ok) {
      const data = await response.json()
      if (data.code === 200 && data.data.ip) {
        return data.data.ip
      }
    }
  } catch (error) {
    console.warn('无法从服务器获取IP:', error)
  }

  try {
    // 方式2: 从外部API获取 (备用)
    const response = await fetch('https://api.ipify.org?format=json', {
      timeout: 3000
    })
    const data = await response.json()
    if (data.ip) {
      return data.ip
    }
  } catch (error) {
    console.warn('无法从外部API获取IP:', error)
  }

  // 方式3: 开发环境使用本地IP
  if (process.env.NODE_ENV === 'development') {
    return '127.0.0.1'
  }

  return null
}

/**
 * 检查IP是否在白名单中
 * @param {string} ip IP地址
 * @param {boolean} useCache 是否使用缓存
 * @returns {Promise<boolean>} 是否允许访问
 */
export async function checkIP(ip, useCache = true) {
  if (!ip) {
    console.error('IP地址为空')
    return false
  }

  // 检查缓存
  if (useCache && ipCheckCache.ip === ip) {
    const now = Date.now()
    if (ipCheckCache.timestamp && (now - ipCheckCache.timestamp) < ipCheckCache.ttl) {
      console.log('使用IP检查缓存:', ipCheckCache.allowed)
      return ipCheckCache.allowed
    }
  }

  try {
    const response = await checkIPWhitelist(ip)

    if (response.code === 200 && response.data) {
      const allowed = response.data.allowed

      // 更新缓存
      ipCheckCache = {
        ip: ip,
        allowed: allowed,
        timestamp: Date.now(),
        ttl: 5 * 60 * 1000
      }

      console.log(`IP ${ip} 检查结果:`, allowed ? '允许' : '拒绝')
      return allowed
    }

    // API调用成功但返回格式不对,默认拒绝
    return false
  } catch (error) {
    console.error('IP检查失败:', error)

    // 在开发环境下,如果检查失败则允许访问
    if (process.env.NODE_ENV === 'development') {
      console.warn('开发环境:IP检查失败,默认允许访问')
      return true
    }

    // 生产环境下,检查失败则拒绝访问
    return false
  }
}

/**
 * 清除IP检查缓存
 */
export function clearIPCheckCache() {
  ipCheckCache = {
    ip: null,
    allowed: null,
    timestamp: null,
    ttl: 5 * 60 * 1000
  }
}

/**
 * 检查是否需要进行IP验证
 * @param {Object} route 路由对象
 * @returns {boolean} 是否需要IP验证
 */
export function needsIPCheck(route) {
  // 如果路由明确设置了skipIPCheck,则跳过检查
  if (route.meta && route.meta.skipIPCheck === true) {
    return false
  }

  // 访问拒绝页面不需要检查
  if (route.name === 'AccessDenied') {
    return false
  }

  // 默认所有页面都需要IP检查
  return true
}
